import React from 'react'
import { Link } from 'react-router-dom'
import { SectionNav } from '../components/SectionNav'
import { usePageTitle } from '../lib/usePageTitle'

const CONSOLE_URL = 'https://app-bg.planesailing.io'

const sectionItems = [
  { id: 'envelope', label: 'Envelope encryption' },
  { id: 'async', label: 'Async ceremonies' },
  { id: 'identity', label: 'Identity & trust' },
  { id: 'peer-to-peer', label: 'Peer-to-peer' },
  { id: 'ceremony', label: 'The ceremony' },
]

interface FeatureCard {
  title: string
  body: string
  tag: string
}

const featureCards: FeatureCard[] = [
  {
    title: 'Envelope encryption, abstracted away',
    body: 'A data key encrypts your secret; that key is Shamir-split and each shard is sealed to a recipient public key. You never touch raw key material — Break Glass orchestrates the envelope for you.',
    tag: 'Cryptography',
  },
  {
    title: 'Quorum, not a single keyholder',
    body: 'A secret is recoverable only when K of N shard-holders cooperate. No one person can unseal alone, and losing one holder does not lock you out.',
    tag: 'Quorum',
  },
  {
    title: 'Identity from your IdP',
    body: 'Recipients authenticate through OIDC. People are who their company says they are — shard distribution is tied to verified identity, not a shared password.',
    tag: 'Zero trust',
  },
]

const featureIcons: Record<string, React.ReactNode> = {
  'Envelope encryption, abstracted away': (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="6" width="14" height="10" rx="2" />
      <path d="M3 7l7 5 7-5" />
    </svg>
  ),
  'Quorum, not a single keyholder': (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="6" cy="7" r="2.4" />
      <circle cx="14" cy="7" r="2.4" />
      <path d="M2.5 16c0-2.2 1.6-3.6 3.5-3.6S9.5 13.8 9.5 16M10.5 16c0-2.2 1.6-3.6 3.5-3.6s3.5 1.4 3.5 3.6" />
    </svg>
  ),
  'Identity from your IdP': (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M10 2L3 5v5c0 4 3.5 6.5 7 8 3.5-1.5 7-4 7-8V5l-7-3z" />
      <circle cx="10" cy="8.5" r="1.8" />
      <path d="M7 14c.4-1.6 1.6-2.4 3-2.4s2.6.8 3 2.4" />
    </svg>
  ),
}

const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'Break Glass',
  applicationCategory: 'SecurityApplication',
  operatingSystem: 'Cloud, Cloudflare Workers',
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'GBP' },
  url: 'https://bg.planesailing.io',
  description:
    'A ceremony orchestrator for your secrets. Shamir-split a data key across a quorum, prove shard possession over time, and reconstruct in the browser. No plaintext ever traverses a server.',
}

const shards = [
  { holder: 'ayana', held: true },
  { holder: 'lena', held: true },
  { holder: 'omar', held: false },
  { holder: 'priya', held: true },
  { holder: 'sven', held: false },
]

function CeremonyStage() {
  return (
    <div className="ceremony-stage" aria-hidden="true">
      <div className="ceremony-vault">
        <div className="ceremony-vault-label">
          <strong>SEALED</strong>
          <span>data key · AES-256</span>
        </div>
      </div>
      <div className="ceremony-shards">
        {shards.map((s, i) => (
          <div key={s.holder} className={`ceremony-shard${s.held ? ' held' : ''}`}>
            <strong>S{i + 1}</strong>
            {s.holder}
          </div>
        ))}
      </div>
      <div className="ceremony-meter">
        <span>
          shards held&nbsp;<span className="quorum">3 / 5</span>
        </span>
        <span className="ceremony-meter-bar">
          <i />
        </span>
        <span className="quorum">quorum K=3</span>
      </div>
    </div>
  )
}

const steps = [
  {
    no: '1',
    title: 'Seal',
    body: 'A data key is generated, Shamir-split into N shards, and each shard is public-key encrypted to one recipient. The opaque pieces are distributed to a quorum — no key ever leaves the browser in the clear.',
  },
  {
    no: '2',
    title: 'Prove',
    body: 'Holders periodically prove they still possess their shard. Drift, departures, and lost devices surface as health before an emergency — not during one.',
  },
  {
    no: '3',
    title: 'Unseal',
    body: 'When the glass breaks, K holders gather their shards. The data key is reconstructed in the requester’s browser and the secret is decrypted locally. The server only ever relayed ciphertext.',
  },
]

export function HomePage() {
  usePageTitle('')

  return (
    <div className="page page-home">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      <section className="hero-section" id="hero">
        <div className="hero-copy reveal">
          <div className="hero-pill emergency">Zero-trust emergency access</div>
          <h1>
            Not another encrypted vault or password manager — a <em>ceremony orchestrator</em> for
            your secrets.
          </h1>
          <p className="hero-lead">
            Break Glass coordinates the cryptographic ceremony of sealing and unsealing a shared
            secret across a quorum of people. It abstracts envelope encryption away entirely — you
            never handle raw keys.
          </p>
          <p className="hero-lead">
            Built for the modern remote, distributed company: seal and unseal happen{' '}
            <b>asynchronously</b>, with no plaintext ever crossing a server.
          </p>
          <div className="hero-actions">
            <a className="button button-solid" href={CONSOLE_URL}>
              Launch console
            </a>
            <Link className="button button-ghost" to="/problems">
              See the problems it solves
            </Link>
          </div>
        </div>

        <div className="hero-panel reveal">
          <div className="window-bar">
            <span />
            <span />
            <span />
            <p>bg.planesailing.io · Ceremony</p>
          </div>
          <CeremonyStage />
        </div>
      </section>

      <SectionNav items={sectionItems} />

      <section className="pain-strip reveal">
        <div className="pain-card">
          <h3>Not a vault</h3>
          <p>It doesn&apos;t hold your secret. It coordinates who can reconstruct it, and when.</p>
        </div>
        <div className="pain-card">
          <h3>Not a password manager</h3>
          <p>No master password, no single custodian. Recovery is a quorum act, by design.</p>
        </div>
        <div className="pain-card">
          <h3>A ceremony orchestrator</h3>
          <p>Seal, prove, and unseal as a coordinated, auditable, asynchronous protocol.</p>
        </div>
        <div className="pain-strip-cta">
          <Link className="button button-ghost" to="/problems">
            Explore the problem breakdown
          </Link>
        </div>
      </section>

      <section className="content-section" id="envelope">
        <div className="section-heading reveal">
          <div className="eyebrow">Envelope encryption · Abstracted</div>
          <h2>Complicated cryptography, taken off your plate.</h2>
          <p className="muted">
            Under the hood, a randomly generated data key encrypts your secret. That key is split
            with Shamir&apos;s Secret Sharing into N shards, and every shard is independently
            encrypted to a recipient&apos;s public key. You never assemble an envelope, manage a KMS,
            or touch raw key material — Break Glass orchestrates all of it.
          </p>
        </div>
        <div className="feature-grid">
          {featureCards.map((card) => (
            <article className="feature-card reveal" key={card.title}>
              <div className="feature-card-icon">{featureIcons[card.title]}</div>
              <div className="tiny-pill feature-card-pill">{card.tag}</div>
              <h3>{card.title}</h3>
              <p>{card.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="content-section content-band" id="async">
        <div className="section-heading reveal">
          <div className="eyebrow">Async ceremonies</div>
          <h2>For the modern remote, distributed company.</h2>
          <p className="muted">
            Classic key ceremonies mean shipping hardware around the physical world or getting
            everyone in a room. That doesn&apos;t work when your quorum spans four time zones. With
            Break Glass, sealing and unsealing happen asynchronously — each participant takes part
            from their own browser, on their own schedule, with no encryption keys travelling the
            real world.
          </p>
        </div>
        <div className="architecture-grid">
          <div className="architecture-points reveal">
            <article>
              <h3>No room required</h3>
              <p>
                Quorum holders never need to assemble in person. The ceremony coordinates over the
                network while every secret operation stays client-side.
              </p>
            </article>
            <article>
              <h3>No shipping keys</h3>
              <p>
                Nothing sensitive is couriered, mailed, or carried. Shards are distributed as opaque
                ciphertext to identities, not to devices in a safe.
              </p>
            </article>
            <article>
              <h3>On everyone&apos;s schedule</h3>
              <p>
                Seal now, gather shards later. Participants contribute when they&apos;re available —
                the protocol holds the state, not a calendar invite.
              </p>
            </article>
          </div>
          <div className="architecture-card reveal">
            <div className="arch-layer arch-primary">
              <strong>Seal (asynchronous)</strong>
              <span>Owner splits the data key and distributes shards to a quorum</span>
            </div>
            <div className="arch-arrow">↓ opaque, per-recipient ciphertext</div>
            <div className="arch-split">
              <div className="arch-layer">
                <strong>Holders, anywhere</strong>
                <span>Each holder receives a shard sealed to their public key</span>
              </div>
              <div className="arch-layer">
                <strong>Relay, never plaintext</strong>
                <span>The server stores metadata and ciphertext only</span>
              </div>
            </div>
            <div className="arch-arrow">↓ when the glass breaks</div>
            <div className="arch-layer">
              <strong>Unseal (asynchronous)</strong>
              <span>K holders contribute shards; the key is rebuilt in the browser</span>
            </div>
          </div>
        </div>
      </section>

      <section className="content-section" id="identity">
        <div className="section-heading reveal">
          <div className="eyebrow">Identity &amp; trust</div>
          <h2>People are who their company says they are.</h2>
          <p className="muted">
            Trust comes from your identity provider. Recipients sign in through OIDC, so every shard
            is bound to a verified person — not a shared credential or an email address anyone could
            spoof. When someone leaves, their identity is gone and the quorum can be re-sealed
            without them.
          </p>
        </div>
        <div className="feature-grid">
          <article className="feature-card reveal">
            <div className="tiny-pill feature-card-pill">OIDC</div>
            <h3>Federated sign-in</h3>
            <p>Authenticate quorum members through the IdP you already run — Okta, Entra, Google, Keycloak, and others.</p>
          </article>
          <article className="feature-card reveal">
            <div className="tiny-pill feature-card-pill">Binding</div>
            <h3>Shards bound to identity</h3>
            <p>Each shard is encrypted to a recipient key tied to a verified identity, not a transferable secret.</p>
          </article>
          <article className="feature-card reveal">
            <div className="tiny-pill feature-card-pill">Audit</div>
            <h3>Attributable actions</h3>
            <p>Every seal, proof, and unseal request is attributed to a real, authenticated person.</p>
          </article>
        </div>
      </section>

      <section className="content-section content-band" id="peer-to-peer">
        <div className="section-heading reveal">
          <div className="eyebrow emergency">Truly peer-to-peer</div>
          <h2>No plaintext ever traverses a server.</h2>
          <p className="muted">
            Not even over a secure TLS connection. Sealing and unsealing happen in the
            participants&apos; browsers; the server only ever sees metadata and opaque ciphertext. It
            cannot reconstruct your secret, and a compromise of the relay reveals nothing usable.
          </p>
        </div>
        <div className="comparison-grid reveal">
          <article className="comparison-panel">
            <div className="panel-label">What the server holds</div>
            <ul>
              <li>Ceremony metadata: quorum size, holders, timestamps.</li>
              <li>Per-recipient encrypted shards — opaque ciphertext.</li>
              <li>Possession proofs and audit events.</li>
            </ul>
          </article>
          <article className="comparison-panel">
            <div className="panel-label">What the server never sees</div>
            <ul>
              <li>The data key — it is reconstructed only in the browser.</li>
              <li>Any shard in the clear — decryption is client-side.</li>
              <li>Your secret. Ever. By construction, not by policy.</li>
            </ul>
          </article>
        </div>
      </section>

      <section className="content-section" id="ceremony">
        <div className="section-heading reveal">
          <div className="eyebrow">The ceremony · How it works</div>
          <h2>Seal → prove → unseal.</h2>
          <p className="muted">
            Picture a safe with a fractured-glass front. Sealing splits the key and scatters the
            shards; possession proofs keep the glass intact and watched; unsealing gathers a quorum
            to break it — and the secret is reassembled where no server can reach.
          </p>
        </div>
        <div className="steps-grid">
          {steps.map((step) => (
            <article className="step-card reveal" key={step.no}>
              <span className="step-no">{step.no}</span>
              <h3>{step.title}</h3>
              <p>{step.body}</p>
            </article>
          ))}
        </div>
        <div className="signup-cta reveal" style={{ marginTop: '32px' }}>
          <div>
            <div className="eyebrow">Ready to run a ceremony?</div>
            <h2>Open the console and seal your first secret.</h2>
            <p className="muted">Free and open source. Self-host on Cloudflare, or use the hosted service.</p>
          </div>
          <div className="cta-actions">
            <a className="button button-solid" href={CONSOLE_URL}>
              Launch console
            </a>
            <Link className="button button-ghost" to="/pricing">
              See pricing
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
